import React, { useMemo, forwardRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Element } from '../element';
import ModalCard from './components/card';
import ModalContext from './context/context';
import * as styles from './modal.styles';

/** @type {typeof import('.').Modal} */
const Modal = forwardRef(
  (
    { className, children, open, onClose, hideClose, mountContent, portalTarget = typeof document !== 'undefined' ? document.body : null, ...props },
    ref,
  ) => {
    const value = useMemo(() => {
      return { onClose, hideClose };
    }, [onClose, hideClose]);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
      setIsMounted(true);
    }, []);

    useEffect(() => {
      if (!open) {
        return () => {};
      }
      const scrollWidth = window.innerWidth - document.body.clientWidth || 0;
      document.documentElement.style.paddingRight = `${scrollWidth}px`;
      document.documentElement.style.overflow = 'hidden';

      const handleClose = (evt) => {
        if (evt.code === 'Escape') {
          onClose?.(evt);
        }
      };
      window.addEventListener('keyup', handleClose);
      return () => {
        document.documentElement.style.paddingRight = '';
        document.documentElement.style.overflow = '';
        window.removeEventListener('keyup', handleClose);
      };
    }, [open, onClose]);

    if (!open && !mountContent) {
      return null;
    }

    const content = (
      <ModalContext.Provider value={value}>
        <Element ref={ref} as="div" className={clsx(styles.modal, className, { [[styles.variants.open]]: open })} {...props}>
          <div data-modal-role="overlay" className={styles.components.overlay} onClick={onClose} role="presentation" />
          {children}
        </Element>
      </ModalContext.Provider>
    );

    return isMounted ? createPortal(content, portalTarget) : null;
  },
);

Modal.Card = ModalCard;

Modal.getCloseSource = (evt) => {
  // @ts-ignore
  return evt.code ? 'keyboard' : evt.target.dataset.modalRole;
};

if (process.env.NODE_ENV !== 'production') {
  Modal.displayName = 'Modal';
}

export default Modal;
